import * as React from 'react'
import { useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { isLoggedInQuerySelector } from 'auth/selectors'
import { algoliaAnalytics } from 'algolia/algolia'
import { useUserQuery } from 'auth/hooks'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { algoliaContentIndex } from 'algolia/algolia-content'

import {
  useContentFavoriteProductMutation,
  useContentIsProductFavoritedQuery,
  useContentUnfavoriteProductMutation,
  contentIsProductFavoritedQueryKey,
  contentCountFavoritesQueryKey,
} from '@web/gen/content'

export function useLikeProduct(productId: string) {
  const queryClient = useQueryClient()
  const { data: isAuthenticated } = useUserQuery(undefined, {
    placeholderData: keepPreviousData,
    select: isLoggedInQuerySelector,
  })
  const [isLiked, setIsLiked] = React.useState(false)
  const { openModal } = useUserAuthModal()
  const { data: isFavorite, isFetching } = useContentIsProductFavoritedQuery(
    { productId },
    {
      select: data => data.contentIsProductFavorited,
      enabled: isAuthenticated ?? false,
    },
  )

  const { mutateAsync: addToFavorites, isPending: isLoadingFavorite } =
    useContentFavoriteProductMutation()
  const { mutateAsync: removeFromFavorites, isPending: isLoadingUnfavorite } =
    useContentUnfavoriteProductMutation()

  React.useEffect(() => {
    isFavorite !== undefined && setIsLiked(isFavorite)
  }, [isFavorite])

  const onClick = React.useCallback(() => {
    // oxlint-disable-next-line no-negated-condition
    if (!isAuthenticated) {
      openModal()
    } else {
      if (isFavorite) {
        setIsLiked(false)
      } else {
        setIsLiked(true)
      }

      try {
        const mutation = isFavorite ? removeFromFavorites : addToFavorites
        mutation({ productId }).then(response => {
          'contentFavoriteProduct' in response && setIsLiked(response.contentFavoriteProduct)
          'contentUnfavoriteProduct' in response && setIsLiked(!response.contentUnfavoriteProduct)
          queryClient.invalidateQueries({
            queryKey: contentIsProductFavoritedQueryKey({ productId }),
          })
          queryClient.invalidateQueries({ queryKey: contentCountFavoritesQueryKey({ productId }) })

          !isFavorite && trackAlgoliaAddedToWishListEvent(productId)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }
  }, [
    openModal,
    queryClient,
    isAuthenticated,
    productId,
    removeFromFavorites,
    addToFavorites,
    isFavorite,
  ])

  return {
    isLiked,
    onClick,
    isLoading: isFetching || isLoadingFavorite || isLoadingUnfavorite,
  }
}

export const trackAlgoliaAddedToWishListEvent = (productId: string) => {
  algoliaAnalytics('clickedObjectIDs', {
    index: algoliaContentIndex,
    eventName: 'Product Added to Wishlist',
    objectIDs: [productId],
  })
}
